import { useCallback, useEffect, useMemo, useRef } from 'react';
import { usePowerSyncDb, usePowerSyncQuery } from '../../../adapters/powersync';
import { useAuth } from '@helios/auth-firebase-web';
import { applyExtractionSymptoms, applyExtractionDiagnoses } from './applyExtractionResults';
import {
  qVoiceRecordingsByVisitSection,
  qLatestExtractionByVisitSection,
  qPatientAllergies,
  softDeleteVoiceRecordingStmt,
  assembleTranscript,
  parseStringArrayColumn,
  compareAllergyMention,
  type VisitVoiceRecordingRow,
  type VisitClinicalExtractionRow,
  type PatientAllergyRow,
  type AllergyMentionMatch,
} from '@helios/data-access';
import type { PatientEncounterRow } from '@helios/data-access';
import { PermissionGate } from '../../../auth/PermissionGate';
import { PERM } from '../../../auth/permissionMap';
import { usePermissions } from '@helios/auth-firebase-web';
import { useClinicalExtraction } from '../../../hooks/useClinicalExtraction';
import { AudioRecordingPlayer } from './AudioRecordingPlayer';
import { NavIcon } from '../../../shell/shared';

/* ------------------------------------------------------------------ */
/*  ChiefComplaintVoicePanel (PRD.271)                                */
/*                                                                    */
/*  Rendered directly below the Chief Complaint text area:            */
/*    Voice recording card(s)  →  read-only AI Clinical Extraction    */
/*                                                                    */
/*  Extraction is an explicit, idempotent server command (never an     */
/*  uncontrolled PowerSync side effect). The panel reads its result    */
/*  from the PowerSync mirror; the hook owns transient run state.      */
/* ------------------------------------------------------------------ */

const SECTION_KEY = 'chief_complaint';
const EXTRACTION_ENABLED = Boolean(import.meta.env.VITE_CLINICAL_EXTRACT_URL);

const DISCLAIMER = 'AI-generated suggestions. Review and confirm before adding to the patient record.';

interface Props {
  encounter: PatientEncounterRow;
  /** Local object URL for a recording still uploading (keyed by recording id). */
  getLocalUrl?: (recordingId: string) => string | null | undefined;
  /** Retry an in-memory blob upload for a failed recording. */
  onRetryUpload?: (recordingId: string) => void;
}

export function ChiefComplaintVoicePanel({ encounter, getLocalUrl, onRetryUpload }: Props) {
  const { db } = usePowerSyncDb();
  const { claims, user } = useAuth();
  const { canAccess } = usePermissions();
  const orgId = claims?.activeOrg ?? '';
  const userId = user?.uid ?? '';
  const visitId = encounter.visit_id;

  const canRunExtraction = canAccess(PERM.clinicalExtractionRun);

  const { data: recordings = [] } = usePowerSyncQuery<VisitVoiceRecordingRow>(
    qVoiceRecordingsByVisitSection(visitId, SECTION_KEY),
  );
  const { data: extractionRows = [] } = usePowerSyncQuery<VisitClinicalExtractionRow>(
    qLatestExtractionByVisitSection(visitId, SECTION_KEY),
  );
  const { data: allergies = [] } = usePowerSyncQuery<PatientAllergyRow>(
    qPatientAllergies(encounter.patient_id),
  );
  const extraction = extractionRows[0] ?? null;

  const extract = useClinicalExtraction();

  const transcript = useMemo(
    () => assembleTranscript(recordings.map((r) => ({ recorded_at: r.recorded_at, transcript: r.transcript }))),
    [recordings],
  );
  const hasTranscript = transcript.trim().length > 0;

  // Apply extracted symptoms + diagnoses into the existing synced tables so
  // they render as "AI Suggested" chips in the Symptoms / Diagnosis sections
  // (mirrors the analyse flow). Guarded per extraction id per mount so the
  // load-time effect and the manual button never double-write.
  const appliedRef = useRef<Set<string>>(new Set());
  const applyEchoes = useCallback(
    async (extractionId: string, symptoms: string[], diagnoses: string[]) => {
      if (!db || !extractionId || appliedRef.current.has(extractionId)) return;
      appliedRef.current.add(extractionId);
      const ctx = {
        orgId,
        clinicId: encounter.clinic_id,
        patientId: encounter.patient_id,
        encounterId: encounter.id,
        userId: userId || null,
        extractionId,
        provider: 'siksha',
      };
      await applyExtractionSymptoms(db, symptoms, ctx);
      await applyExtractionDiagnoses(db, diagnoses, ctx);
    },
    [db, orgId, userId, encounter.clinic_id, encounter.patient_id, encounter.id],
  );

  // Auto-apply from a completed extraction row (covers the cached / previously
  // run case on load) — like analyse, the result populates the structured
  // sections without the clinician re-typing it.
  useEffect(() => {
    if (extraction && extraction.status === 'completed' && extraction.id) {
      void applyEchoes(
        extraction.id,
        parseStringArrayColumn(extraction.symptoms_json),
        parseStringArrayColumn(extraction.diagnoses_json),
      );
    }
  }, [extraction, applyEchoes]);

  const runExtraction = useCallback(() => {
    if (!hasTranscript) return;
    const latest = recordings[recordings.length - 1];
    void (async () => {
      const summary = await extract.extract({
        orgId,
        patientId: encounter.patient_id,
        visitId,
        sectionKey: SECTION_KEY,
        recordingId: latest?.id,
        transcript,
      });
      // Apply immediately from the API response (no sync wait), mirroring analyse.
      if (summary) await applyEchoes(summary.extractionId, summary.symptoms, summary.diagnoses);
    })();
  }, [extract, hasTranscript, recordings, orgId, encounter.patient_id, visitId, transcript, applyEchoes]);

  const handleDelete = useCallback(
    async (id: string) => {
      if (!db) return;
      const stmt = softDeleteVoiceRecordingStmt({ id, now: new Date().toISOString() });
      await db.execute(stmt.sql, stmt.params);
    },
    [db],
  );

  // Nothing to show at all — keep the section quiet until the first recording.
  if (recordings.length === 0 && !extraction) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)', marginTop: 'var(--sp-3)' }}>
      {/* ── Recording card(s), chronological ── */}
      <PermissionGate module={PERM.voiceRecordingView.module} subModule={PERM.voiceRecordingView.subModule}>
        {recordings.map((rec) => (
          <AudioRecordingPlayer
            key={rec.id}
            recording={rec}
            localBlobUrl={getLocalUrl?.(rec.id) ?? null}
            canDelete={canAccess(PERM.voiceRecordingDelete)}
            onDelete={() => void handleDelete(rec.id)}
            onRetryUpload={() => onRetryUpload?.(rec.id)}
          />
        ))}
      </PermissionGate>

      {/* ── AI Clinical Extraction (read-only in Phase 1) ── */}
      {EXTRACTION_ENABLED && (
        <PermissionGate
          module={PERM.clinicalExtractionView.module}
          subModule={PERM.clinicalExtractionView.subModule}
        >
          <div
            style={{
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              padding: 'var(--sp-3)',
              background: 'var(--bg-surface)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--sp-2)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--font-size-xs)', fontWeight: 600 }}>
                <NavIcon name="sparkles" /> AI Clinical Extraction
              </div>
              {canRunExtraction && hasTranscript && (
                <button
                  type="button"
                  onClick={runExtraction}
                  disabled={extract.status === 'submitting'}
                  style={pillBtnStyle}
                >
                  {extract.status === 'submitting'
                    ? 'Extracting…'
                    : extraction
                      ? 'Refresh'
                      : 'Extract clinical details'}
                </button>
              )}
            </div>

            <ExtractionBody
              extraction={extraction}
              allergies={allergies}
              submitting={extract.status === 'submitting'}
              error={extract.status === 'error' ? extract.error : null}
              retryable={extract.retryable}
              onRetry={runExtraction}
              hasTranscript={hasTranscript}
            />

            <div
              aria-live="polite"
              style={{ fontSize: 'var(--font-size-2xs, 10px)', color: 'var(--text-muted)', fontStyle: 'italic' }}
            >
              {DISCLAIMER}
            </div>
          </div>
        </PermissionGate>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function ExtractionBody({
  extraction,
  allergies,
  submitting,
  error,
  retryable,
  onRetry,
  hasTranscript,
}: {
  extraction: VisitClinicalExtractionRow | null;
  allergies: PatientAllergyRow[];
  submitting: boolean;
  error: string | null;
  retryable: boolean;
  onRetry: () => void;
  hasTranscript: boolean;
}) {
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;

  if (submitting) {
    return <Muted>Extracting clinical details…</Muted>;
  }
  if (error) {
    return (
      <div style={{ fontSize: 'var(--font-size-xs)', color: '#dc2626', display: 'flex', gap: 8, alignItems: 'center' }}>
        <span>{error}</span>
        {retryable && (
          <button type="button" onClick={onRetry} style={linkBtnStyle}>
            Retry
          </button>
        )}
      </div>
    );
  }
  if (offline && !extraction) {
    return <Muted>Offline — extraction runs when you reconnect.</Muted>;
  }
  if (!extraction) {
    return (
      <Muted>
        {hasTranscript ? 'No extraction yet.' : 'Record a chief complaint to enable extraction.'}
      </Muted>
    );
  }
  if (extraction.status === 'processing') {
    return <Muted>Extraction in progress…</Muted>;
  }
  if (extraction.status === 'failed') {
    return <Muted>Last extraction failed{extraction.error_code ? ` (${extraction.error_code})` : ''}.</Muted>;
  }

  const symptoms = parseStringArrayColumn(extraction.symptoms_json);
  const diagnoses = parseStringArrayColumn(extraction.diagnoses_json);
  const allergyMentions = parseStringArrayColumn(extraction.allergies_json);
  const existingSubstances = allergies.map((a) => a.substance);

  const isEmpty =
    !extraction.summary && symptoms.length === 0 && diagnoses.length === 0 && allergyMentions.length === 0;
  if (isEmpty) return <Muted>No clinical details were extracted.</Muted>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
      {extraction.summary && (
        <Field label="Summary">
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text)' }}>{extraction.summary}</div>
        </Field>
      )}
      {symptoms.length > 0 && (
        <Field label="Symptoms">
          <ChipRow items={symptoms.map((s) => ({ label: s }))} />
        </Field>
      )}
      {diagnoses.length > 0 && (
        <Field label="Possible diagnoses">
          <ChipRow items={diagnoses.map((d) => ({ label: d }))} />
        </Field>
      )}
      {allergyMentions.length > 0 && (
        <Field label="Allergies mentioned">
          <ChipRow
            items={allergyMentions.map((a) => ({
              label: a,
              note: allergyNote(compareAllergyMention(a, existingSubstances)),
            }))}
          />
        </Field>
      )}
    </div>
  );
}

function allergyNote(match: AllergyMentionMatch): string | undefined {
  switch (match) {
    case 'already_recorded':
      return 'Already recorded';
    case 'possible_duplicate':
      return 'Possible duplicate';
    default:
      return 'New mention';
  }
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ fontSize: 'var(--font-size-2xs, 10px)', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function ChipRow({ items }: { items: { label: string; note?: string }[] }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {items.map((it, i) => (
        <span
          key={`${it.label}-${i}`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '2px 8px',
            borderRadius: 999,
            border: '1px solid var(--border)',
            background: 'var(--bg-muted, var(--bg-surface))',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text)',
          }}
        >
          {it.label}
          {it.note && (
            <span style={{ fontSize: 'var(--font-size-2xs, 10px)', color: 'var(--text-muted)' }}>· {it.note}</span>
          )}
        </span>
      ))}
    </div>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>{children}</div>;
}

const pillBtnStyle: React.CSSProperties = {
  height: 28,
  padding: '0 10px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  color: 'var(--text)',
  cursor: 'pointer',
  fontSize: 'var(--font-size-xs)',
};

const linkBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  color: 'var(--color-primary)',
  cursor: 'pointer',
  fontSize: 'var(--font-size-xs)',
  textDecoration: 'underline',
};
